import { useLingui } from "@lingui/react";
import { RTL_LANGUAGES } from "../utils/rtl-helpers";

/**
 * Hook to detect RTL languages and provide text alignment utilities
 */
export const useRTL = () => {
  const { i18n } = useLingui();

  const isRTL = RTL_LANGUAGES.includes(i18n.locale);
  const isArabic = i18n.locale.startsWith("ar");

  return {
    isRTL,
    isArabic,
    locale: i18n.locale,

    // Text alignment utilities
    textAlign: isRTL ? "text-right" : "text-left",
    textAlignOpposite: isRTL ? "text-left" : "text-right",
    textDirection: isRTL ? "rtl" : "ltr",

    // Flexbox utilities
    justifyContent: isRTL ? "justify-end" : "justify-start",
    justifyContentOpposite: isRTL ? "justify-start" : "justify-end",

    // Margin/padding utilities
    marginLeft: isRTL ? "mr-" : "ml-",
    marginRight: isRTL ? "ml-" : "mr-",
    paddingLeft: isRTL ? "pr-" : "pl-",
    paddingRight: isRTL ? "pl-" : "pr-",

    // Border utilities
    borderLeft: isRTL ? "border-r-" : "border-l-",
    borderRight: isRTL ? "border-l-" : "border-r-",

    // Utility functions
    getTextAlign: (center = false) => {
      if (center) return "text-center";
      return isRTL ? "text-right" : "text-left";
    },

    getFlexAlign: (center = false) => {
      if (center) return "justify-center";
      return isRTL ? "justify-end" : "justify-start";
    },

    // CSS-in-JS style object
    getTextStyle: (center = false): React.CSSProperties => ({
      textAlign: center ? "center" : (isRTL ? "right" : "left"),
      direction: isRTL ? "rtl" : "ltr",
    }),

    // Class name generator
    getAlignmentClasses: (additionalClasses = "") => {
      const baseClasses = isRTL ? "text-right rtl" : "text-left ltr";
      return `${baseClasses} ${additionalClasses}`.trim();
    },
  };
};
